// src/hooks/useProvider.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getProviderByUid, updateProvider } from '@/services/providers';
import type { Provider } from '@/types';

export function useProvider(uid: string | undefined) {
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState<boolean>(!!uid);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getProviderByUid(uid);
      setProvider(data);
    } catch (e: unknown) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('useProvider: failed to load provider', e);
      }
      if (e instanceof Error) setError(e.message);
      else if (typeof e === 'string') setError(e);
      else setError('Failed to load provider');
    } finally {
      setLoading(false);
    }
  }, [uid]);

  const patch = useCallback(
    async (p: Partial<Provider>) => {
      if (!uid) return;
      await updateProvider(uid, p);
      await refresh();
    },
    [uid, refresh],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return useMemo(
    () => ({ provider, loading, error, refresh, patch }),
    [provider, loading, error, refresh, patch],
  );
}
